import type { TagSortOrder } from '@mnd-frontend/graphql-schema';
import {
  Button,
  Checkbox,
  FormField,
  Icons,
  Input,
  InputRow,
} from '@mnd-frontend/ui/publish/components';
import { NEUTRAL_WHITE } from '@mnd-frontend/ui/shared/tokens';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTagIdsQuery } from '../../../generated/hooks';

const Header = styled.div`
  background: ${NEUTRAL_WHITE};
`;
const StyledFormField = styled(FormField)`
  width: 100%;
`;
const CustomInputRow = styled.div`
  margin-top: 0.75rem;
`;

const MobileHeader = ({
  totalCount,
  selectedIds,
  searchQuery,
  pressroomId,
  selectedSort,
  onCombineClick,
  onDeleteClick,
  onSelectedIdsChanged,
  onSearchQueryChanged,
}: {
  totalCount: number;
  searchQuery: string;
  pressroomId: string;
  selectedIds: string[];
  selectedSort: TagSortOrder;
  onCombineClick: () => void;
  onDeleteClick: () => void;
  onSelectedIdsChanged: (ids: string[]) => void;
  onSearchQueryChanged: (query: string) => void;
}) => {
  const [t] = useTranslation();
  const { refetch: getTagIds } = useTagIdsQuery({ skip: true });
  const [expanded, setExpanded] = useState(false);

  return (
    <Header>
      <InputRow $nowrap>
        <StyledFormField id="search-tags" label={t('category_management_search_label')} hideLabels>
          <Input
            trailingIcon={<Icons.Search />}
            label={t('category_management_search_placeholder')}
            onChange={e => onSearchQueryChanged(e.target.value)}
            value={searchQuery}
          />
        </StyledFormField>
        <Button
          aria-label={t('card_action-expand')}
          icon={<Icons.Sliders />}
          type="button"
          configuration="toned"
          onClick={() => setExpanded(e => !e)}
        />
      </InputRow>
      {(expanded || selectedIds.length > 0) && (
        <InputRow $grow>
          <CustomInputRow>
            <Checkbox
              onChange={async e => {
                if (e.target.checked) {
                  try {
                    const result = await getTagIds({
                      pressroomId,
                      query: searchQuery,
                      perPage: totalCount,
                      sortOrder: selectedSort,
                      page: 1,
                    });
                    onSelectedIdsChanged(
                      result.data.pressroom?.tags2.hits.map(tag => tag.id) ?? [],
                    );
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error(err);
                  }
                } else {
                  onSelectedIdsChanged([]);
                }
              }}
              state={
                selectedIds.length === 0
                  ? 'unchecked'
                  : selectedIds.length === totalCount
                    ? 'checked'
                    : 'indeterminate'
              }
              label={t('bulk-actions_selected-count', {
                totalCount,
                count: selectedIds.length,
              })}
            />
          </CustomInputRow>
          {selectedIds.length > 0 && (
            <InputRow $justifyContent="start">
              {selectedIds.length > 1 && (
                <Button
                  configuration="text"
                  type="button"
                  onClick={onCombineClick}
                  disabled={selectedIds.length < 2}
                >
                  {t('category_management_combine')}
                </Button>
              )}
              <Button
                configuration="text"
                type="button"
                disabled={selectedIds.length === 0}
                onClick={onDeleteClick}
              >
                {t('common_delete')}
              </Button>
            </InputRow>
          )}
        </InputRow>
      )}
    </Header>
  );
};

export default React.memo(MobileHeader);
